import { FC, useState } from 'react';

import { Button, Checkbox, Heading, Notification } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import HtmlContent from '@telialt/common-html-content/src';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';

import { useAcceptMarketingConsentMutation } from '../../../../hooks/useAcceptMarketingConsentMutation';
import { useCommitmentChange } from '../../../../hooks/useCommitmentChange';
import { Commitment, OfferGroup } from '../../../../model';
import { PLANS_TEST_IDS } from '../../../../test-ids';
import { ButtonsWrapper, ConsentWrapper } from './NboConsent.styles';

type Props = {
    possibleCommitmentTypes: Commitment[];
    agreementId: string;
    msisdn: string;
    offerGroup: OfferGroup;
};

export const NboConsent: FC<Props> = ({ possibleCommitmentTypes, agreementId, msisdn, offerGroup }) => {
    const { t } = useTranslations();
    const [consentsAccepted, setConsentsAccepted] = useState(false);

    const { mutate, isPending, error } = useAcceptMarketingConsentMutation();
    useLogError(error, DDComponent.Plans);

    const { handleCommitmentChange, hasAnotherCommitment } = useCommitmentChange(possibleCommitmentTypes, offerGroup);

    const handleAcceptConsents = () => {
        mutate({
            businessAgreementId: agreementId,
            contractId: msisdn,
            specificationId: offerGroup,
        });
    };

    return (
        <>
            <Notification status="warning" headingTag="h2" heading={t['plans.plans_list.consent.notification.title']}>
                {t['plans.plans_list.consent.notification.text']}
            </Notification>
            <ConsentWrapper>
                <Heading tag="h3" variant="title-100">
                    {t['plans.plans_list.consent.heading']}
                </Heading>
                <HtmlContent content={t['plans.plans_list.consent.text']} />
                <Checkbox
                    data-test={PLANS_TEST_IDS.CONSENT_CHECKBOX}
                    id="consent"
                    name="consent"
                    checked={consentsAccepted}
                    onChange={() => setConsentsAccepted(!consentsAccepted)}
                    label={t['plans.plans_list.consent.checkbox.label']}
                    disabled={isPending}
                />
                {error && !isPending && (
                    <Notification
                        headingTag="h3"
                        heading={t['plans.plans_list.consent.error.notification']}
                        status="error"
                    />
                )}
                <ButtonsWrapper>
                    <Button
                        variant="primary"
                        disabled={!consentsAccepted}
                        data-test={PLANS_TEST_IDS.ACCEPT_CONSENT_BUTTON}
                        loading={isPending}
                        onClick={handleAcceptConsents}
                    >
                        {t['plans.plans_list.consent.accept.button']}
                    </Button>
                    {hasAnotherCommitment && (
                        <Button
                            variant="secondary"
                            data-test={PLANS_TEST_IDS.VIEW_OFFERS_BUTTON}
                            onClick={handleCommitmentChange}
                        >
                            {t['plans.plans_list.consent.view_offers.button']}
                        </Button>
                    )}
                </ButtonsWrapper>
            </ConsentWrapper>
        </>
    );
};
